import { getToken, setToken } from './backend';
import { currentAccount, isAuthConfigured, refreshToken, signInWithHost } from './auth';

/**
 * The session token, resolved at the MOMENT OF USE rather than captured.
 *
 * WHY THIS EXISTS — a token read once and kept is a token that expires. The CRDT WebSocket used to
 * be handed the string `resolveSession()` snapshotted at page load (App.tsx's `useMemo(…, [])`),
 * while AuthGate's periodic top-up wrote its renewals only to sessionStorage. REST re-read that
 * store on every call and kept working; the WebSocket never did. So after the Entra access token's
 * ~60-90 min lifetime, EVERY room — including a part opened seconds ago — presented a dead
 * credential, the backend refused the handshake, and @hocuspocus/provider detached the room for
 * good (`permissionDeniedHandler` → `disconnect()` → `providerMap.delete(name)`; a 4401 close sets
 * `shouldConnect = false`). Nothing short of a new renderer could recover, which in Niobe Desktop
 * meant quitting from the tray: closing the window only hides it.
 *
 * The provider re-evaluates a FUNCTION token on every `onOpen`, so `token: () => freshToken()` puts
 * a live credential on every reconnect attempt. That is the fix; everything else here supports it.
 *
 * WHY IT IS BOUNDED — Hocuspocus `await`s this inside `onOpen`. In the desktop the renewal goes
 * through the shell's host bridge, which can in principle open a browser window, so an unbounded
 * wait would hang the handshake rather than fail it. Timing out into the cached token lets the
 * normal refusal path run, which is recoverable; a hung `onOpen` is not.
 */

/** Renew this far ahead of `exp`, matching the desktop shell's own skew (host-auth.js). */
const SKEW_MS = 120_000;

/** Longest we will wait for a renewal before falling back to what we already hold. */
const RENEWAL_TIMEOUT_MS = 10_000;

/**
 * `exp` in milliseconds, or null when the token carries none.
 *
 * Null is not an error: in dev the token is the stub identity `"Name|Role"`, which is not a JWT at
 * all. `usable()` decides what null means, because that depends on whether auth is configured.
 */
function expiresAt(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))) as {
      exp?: number;
    };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Can this token still be presented? */
function usable(token: string | null): token is string {
  if (!token) return false;
  // Dev/stub identity: not a JWT, never expires, and there is no IdP to renew it with.
  if (!isAuthConfigured()) return true;
  const at = expiresAt(token);
  // Auth IS configured but we cannot read an expiry — a malformed or truncated token. Renewing is
  // the safe reading: presenting it would be refused anyway.
  if (at === null) return false;
  return at - SKEW_MS > Date.now();
}

/** Resolve with null instead of hanging. The renewal itself is not cancellable; we stop waiting. */
async function withTimeout(p: Promise<string | null>, ms: number): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      p,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * "A NEW token is in hand." Fired when a renewal, or the member signing in again, replaced the
 * token we held — never for a renewal that fell back to the same one.
 *
 * WHY IT EXISTS — a room whose handshake was refused retried a few times on a backoff and then
 * gave up, because each retry renewed into the same (failed) token. After a sleep the network is
 * usually what failed, so the renewal that finally works comes LATER, from somewhere else: the
 * wake-up recovery, a REST 401, the session-expired banner. Canvas subscribes, so a refused room
 * retries the moment a usable credential exists, whoever obtained it.
 */
type RenewedListener = (token: string) => void;
const renewedListeners = new Set<RenewedListener>();

export function onTokenRenewed(cb: RenewedListener): () => void {
  renewedListeners.add(cb);
  return () => {
    renewedListeners.delete(cb);
  };
}

function adopt(token: string, held: string | null): void {
  setToken(token);
  if (token === held) return;
  for (const cb of [...renewedListeners]) {
    try {
      cb(token);
    } catch {
      /* one listener must not stop the others */
    }
  }
}

/**
 * One renewal at a time. Several rooms reconnecting together (a resumed machine reopens every
 * provider at once) must not each drive a separate token acquisition — the desktop host caches its
 * own in-flight promise for exactly this reason, and MSAL's silent renewal is an iframe.
 */
let inFlight: Promise<string> | null = null;

async function acquire(): Promise<string> {
  // What we fall back to if renewal fails: the token we already hold. It may be expired, in which
  // case the handshake is refused and the caller's recovery path runs — an honest failure, rather
  // than a connection opened with no credential at all.
  const held = getToken() ?? '';
  try {
    const renewed = await withTimeout(refreshToken(), RENEWAL_TIMEOUT_MS);
    if (renewed) {
      adopt(renewed, getToken());
      return renewed;
    }
  } catch {
    // refreshToken() already swallows its own failures and returns null; this is belt and braces.
  }
  return held;
}

/** Force a renewal even if what we hold still looks valid. Use on a 401 / a refused handshake. */
export async function renewToken(): Promise<string> {
  const held = getToken() ?? '';
  // Nothing renewable. Either there is no IdP at all (dev/stub identity), or nobody has signed in
  // yet — silent renewal extends an EXISTING session, and the initial interactive sign-in belongs
  // to AuthGate, which gates the whole app on it. Attempting one here would be a guaranteed-null
  // round trip through MSAL on every call, which is both slower and a lie about what we tried.
  if (!isAuthConfigured() || !currentAccount()) return held;
  inFlight ??= acquire().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** The token, guaranteed usable at the moment of the call — renewing only when it is not. */
export async function freshToken(): Promise<string> {
  const held = getToken();
  if (usable(held)) return held;
  return renewToken();
}

/**
 * The member clicked "Sign in" on the session-expired banner (Niobe Desktop). Opens the browser
 * through the shell and, once signed in, hands the new token to everything waiting for one (see
 * `onTokenRenewed`). Resolves false when the sign-in did not complete.
 */
export async function signInAgain(): Promise<boolean> {
  const result = await signInWithHost();
  if (!result) return false;
  adopt(result.token, getToken());
  return true;
}
